/**
 * CreditEngineLetterDrafts — /admin/credit-engine/letter-drafts
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * STATUS_COLOR pastel map (`#86EFAC` / `#93C5FD` / `#A78BFA` /
 * `#FCA5A5` / `#FDE68A`) REMOVED. Status pills now emit `Badge` with
 * `shape="sharp"` + `STATUS_TONE` map. Modal restyled to hairline
 * admin chrome (sharp radii, `--ds-color-surface`, no `#141420`
 * dark-mode background). AdminLayout wrap replaces the inline
 * div/h1 header.
 *
 * Business logic (list + stats + detail fetch + cost formula
 * `(prompt/1M * 1.25 + completion/1M * 10)`) preserved BYTE-EXACT.
 * All testids unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, X,
  FileText, Filter,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { GhostButton, Section, SelectInput, Stat } from "@/admin/_primitives";

const STATUS_TONE = {
  ready: "successMuted",
  edited: "info",
  finalized: "info",
  failed: "danger",
  generating: "warning",
};





export default function AdminCreditEngineLetterDrafts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (modeFilter) params.set("mode", modeFilter);
      params.set("limit", "30");
      const [{ data }, { data: statsData }] = await Promise.all([
        api.get(`/admin/credit-engine/letter-drafts?${params.toString()}`),
        api.get(`/admin/credit-engine/letter-drafts/stats`),
      ]);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setStats(statsData || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, modeFilter]);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = useCallback(async (id) => {
    setOpenId(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/credit-engine/letter-drafts/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setOpenId(null);
    }
  }, []);

  const statusEntries = Object.entries(stats?.by_status || {});

  return (
    <AdminLayout
      title="Letter drafts (hidden preview)"
      subtitle="Phase 5B — direct URL only, not in nav."
      actions={
        <GhostButton
          onClick={reload}
          data-testid="admin-letter-drafts-reload-btn"
          aria-label="Reload"
          className="!py-1 !text-[11px]"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
        </GhostButton>
      }
    >
      <div data-testid="admin-letter-drafts">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {statusEntries.map(([k, v], idx) => (
              <Stat
                key={k}
                testid={`admin-letter-drafts-stat-${k}`}
                label={k}
                value={v}
                highlight={idx === 0}
              />
            ))}
            {stats.cost_summary && (
              <Stat
                testid="admin-letter-drafts-cost"
                label="Est. cost (all drafts)"
                value={`$${stats.cost_summary.estimated_usd_total?.toFixed?.(4) ?? "0.0000"}`}
                highlight
              />
            )}
          </div>
        )}

        <Section title="Filter" icon={Filter} testid="admin-letter-drafts-filters">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Status
              </span>
              <SelectInput
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="admin-letter-drafts-status-filter"
              >
                <option value="">All statuses</option>
                <option value="ready">Ready</option>
                <option value="edited">Edited</option>
                <option value="finalized">Finalized</option>
                <option value="failed">Failed</option>
              </SelectInput>
            </label>
            <label className="block">
              <span
                className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                style={{ color: "var(--ds-color-ink-subtle)" }}
              >
                Mode
              </span>
              <SelectInput
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                data-testid="admin-letter-drafts-mode-filter"
              >
                <option value="">All modes</option>
                <option value="bureau_dispute">Bureau dispute</option>
                <option value="furnisher_dispute">Furnisher dispute</option>
                <option value="inquiry_dispute">Inquiry dispute</option>
                <option value="identity_theft_dispute">Identity theft</option>
                <option value="mov_followup">MOV follow-up</option>
                <option value="results_followup">Results follow-up</option>
              </SelectInput>
            </label>
          </div>
        </Section>

        {loading ? (
          <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-letter-drafts-empty"
            className="text-[13px] py-6 text-center"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            No letter drafts yet.
          </div>
        ) : (
          <Section
            title="Drafts"
            icon={FileText}
            testid="admin-letter-drafts-table-section"
            bodyClassName="p-0"
            badge={
              <span
                className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                {items.length} / {total}
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr
                    className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                    style={{
                      color: "var(--ds-color-ink-subtle)",
                      background: "var(--ds-color-admin-surface-muted)",
                      borderBottom: "1px solid var(--ds-color-admin-border)",
                    }}
                  >
                    <th className="px-4 py-2 font-semibold">Mode</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold text-right">Score</th>
                    <th className="px-4 py-2 font-semibold">Hallucination</th>
                    <th className="px-4 py-2 font-semibold">Report</th>
                    <th className="px-4 py-2 font-semibold text-right">Est USD</th>
                    <th className="px-4 py-2 font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const cost = ((it.prompt_tokens_estimated || 0) / 1_000_000) * 1.25 + ((it.completion_tokens_estimated || 0) / 1_000_000) * 10;
                    const isLast = idx === items.length - 1;
                    return (
                      <tr
                        key={it.id}
                        onClick={() => openDetail(it.id)}
                        data-testid={`admin-letter-drafts-row-${it.id}`}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                          transitionDuration: "var(--ds-duration-fast)",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td
                          className="px-4 py-1.5 text-[12px] font-mono"
                          style={{ color: "var(--ds-color-ink)" }}
                        >
                          {it.mode}
                        </td>
                        <td className="px-4 py-1.5">
                          <Badge
                            tone={STATUS_TONE[it.status] || "neutralMuted"}
                            size="sm"
                            shape="sharp"
                            className="uppercase tracking-[0.14em]"
                          >
                            {it.status}
                          </Badge>
                        </td>
                        <td
                          className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink)" }}
                        >
                          {it.quality_score}
                        </td>
                        <td className="px-4 py-1.5 text-[12px]">
                          <span className="inline-flex items-center gap-1.5">
                            {it.hallucination_status === "passed" ? (
                              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--ds-color-success-muted)" }} />
                            ) : it.hallucination_status === "rejected_hallucination" ? (
                              <AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--ds-color-warning)" }} />
                            ) : (
                              <XCircle className="w-3.5 h-3.5" style={{ color: "var(--ds-color-danger)" }} />
                            )}
                            <span style={{ color: "var(--ds-color-ink-muted)" }}>{it.hallucination_status}</span>
                          </span>
                        </td>
                        <td
                          className="px-4 py-1.5 text-[11px] font-mono"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {(it.report_id || "").slice(-8)}
                        </td>
                        <td
                          className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink)" }}
                        >
                          ${cost.toFixed(4)}
                        </td>
                        <td
                          className="px-4 py-1.5 text-[12px] tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {(it.created_at || "").slice(0, 19)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {openId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(27, 26, 22, 0.55)" }}
            onClick={() => setOpenId(null)}
            role="dialog"
            data-testid="admin-letter-drafts-detail-modal"
          >
            <div
              className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-admin-border)",
                boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <header
                className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 border-b"
                style={{ background: "var(--ds-color-surface)", borderColor: "var(--ds-color-admin-border)" }}
              >
                <Heading level={4} size="sm" weight="semibold" className="leading-none">
                  Letter draft detail
                </Heading>
                <button
                  onClick={() => setOpenId(null)}
                  className="transition-colors"
                  style={{ color: "var(--ds-color-ink-muted)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
                >
                  <X className="w-4 h-4" />
                </button>
              </header>
              <div className="p-4 space-y-3">
                {!detail ? (
                  <div style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>
                ) : (
                  <>
                    <div
                      className="text-[11px] font-mono tabular-nums"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      id: {detail.id} · mode: {detail.mode} · status: {detail.status} · score: {detail.quality_score}
                    </div>
                    <div>
                      <div
                        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        Recipient
                      </div>
                      <div className="text-[13px]" style={{ color: "var(--ds-color-ink)" }}>
                        {detail.recipient?.org_name} — {detail.recipient?.street} {detail.recipient?.city_state_zip}
                      </div>
                    </div>
                    <div>
                      <div
                        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        Rendered body
                      </div>
                      <pre
                        data-testid="admin-letter-draft-body"
                        className="whitespace-pre-wrap font-mono text-[11px] p-3 rounded-[var(--ds-radius-input)] max-h-96 overflow-auto"
                        style={{
                          background: "var(--ds-color-admin-surface-muted)",
                          border: "1px solid var(--ds-color-admin-border)",
                          color: "var(--ds-color-ink-muted)",
                        }}
                      >
                        {detail.rendered_body || "(not rendered)"}
                      </pre>
                    </div>
                    <div>
                      <div
                        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        Hallucination check
                      </div>
                      <pre
                        className="whitespace-pre-wrap font-mono text-[11px] p-3 rounded-[var(--ds-radius-input)]"
                        style={{
                          background: "var(--ds-color-admin-surface-muted)",
                          border: "1px solid var(--ds-color-admin-border)",
                          color: "var(--ds-color-ink-muted)",
                        }}
                      >
                        {JSON.stringify(detail.hallucination_report || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div
                        className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        Quality checks
                      </div>
                      <ul className="text-[13px] space-y-1">
                        {(detail.quality_checks || []).map((c) => (
                          <li key={c.key} className="flex items-start gap-1.5">
                            {c.passed ? (
                              <CheckCircle2 className="w-3 h-3 mt-1" style={{ color: "var(--ds-color-success-muted)" }} />
                            ) : (
                              <XCircle className="w-3 h-3 mt-1" style={{ color: "var(--ds-color-warning)" }} />
                            )}
                            <span>
                              <span style={{ color: "var(--ds-color-ink)" }}>{c.key}</span>{" "}
                              <span style={{ color: "var(--ds-color-ink-muted)" }}>
                                — {c.passed ? "OK" : c.remediation}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
